import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Minus, Plus, Truck, ShieldCheck, Recycle, ArrowLeft, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import CategoriesSubnav from "@/components/CategoriesSubnav";
import JsonLd from "@/components/JsonLd";
import { optimizeImage, optimizeImageSrcSet } from "@/lib/images";
import { getCategoryRouteForSlug, getCategoryLabelForSlug } from "@/lib/categoryRoutes";

const SITE_URL = "https://cartonmove.fr";

const buildProductSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  image: product.image,
  description:
    product.seo_description || product.short_description || product.description,
  sku: product.id,
  mpn: product.slug,
  brand: { "@type": "Brand", name: "cartonmove" },
  category: product.category,
  offers: {
    "@type": "Offer",
    url: `${SITE_URL}/produit/${product.slug}`,
    priceCurrency: "EUR",
    price: product.price.toFixed(2),
    availability:
      product.stock > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
    seller: { "@type": "Organization", name: "cartonmove" },
    areaServed: { "@type": "AdministrativeArea", name: "Île-de-France" },
  },
});

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addItem, setOpen } = useCart();

  useDocumentTitle(
    product ? `${product.seo_title || `${product.name} — cartonmove`}` : "Produit — cartonmove",
    product ? product.seo_description || product.short_description : "",
    product && product.image
      ? {
          image: `https://wsrv.nl/?url=${encodeURIComponent(
            product.image.replace(/^https?:\/\//, "")
          )}&w=1200&h=630&output=jpg&q=85&we&fit=cover`,
          type: "product",
        }
      : {}
  );

  useEffect(() => {
    setLoading(true);
    setQty(1);
    fetchProduct(slug)
      .then((p) => setProduct(p))
      .catch(() => setError("Produit introuvable"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-cdm py-20 text-center">Chargement…</div>;
  if (error || !product)
    return (
      <div className="container-cdm py-20 text-center" data-testid="product-not-found">
        <h1 className="font-display font-extrabold text-[28px] sm:text-[33px] md:text-[48px]">Produit introuvable</h1>
        <Link
          to="/cartons-demenagement"
          className="btn-primary mt-6 inline-flex"
          data-testid="product-not-found-back"
        >
          Retour à la boutique
        </Link>
      </div>
    );

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} ajouté(s) au panier`, {
      action: { label: "Voir le panier", onClick: () => setOpen(true) },
    });
  };

  const updateQty = (delta) => setQty((q) => Math.max(1, q + delta));

  const backRoute = getCategoryRouteForSlug(product.slug);
  const backLabel = getCategoryLabelForSlug(product.slug);

  return (
    <div data-testid="product-detail-page">
      <JsonLd id="ld-product" data={buildProductSchema(product)} />
      {slug === "carton-standard-renforce" && <CategoriesSubnav />}
      <div className="container-cdm pt-5">
        <Link
          to={backRoute}
          className="inline-flex items-center gap-2 text-sm text-[#5b6b7a] hover:text-[#0f1d2b]"
          data-testid="product-back-button"
        >
          <ArrowLeft className="h-4 w-4" /> Retour à {backLabel}
        </Link>
      </div>

      {/* TOP — Image + Info + Qty + Ajouter (compact) */}
      <section className="container-cdm py-6 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-6">
          <div className="aspect-[4/3] max-h-[420px] bg-[#F3EFE9] overflow-hidden rounded-2xl">
            <img
              src={optimizeImage(product.image, 900)}
              srcSet={optimizeImageSrcSet(product.image, [480, 768, 1024, 1280])}
              sizes="(max-width: 1024px) 100vw, 50vw"
              alt={product.name}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className={`w-full h-full object-contain ${
                product.image_zoom === "large"
                  ? "p-1"
                  : product.image_zoom === "small"
                  ? "p-8"
                  : "p-4"
              }`}
            />
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="overline">{product.category}</div>
          <h1
            className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] mt-2 text-[#0f1d2b] leading-tight"
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <p className="mt-3 text-[14px] text-[#3a4a5a] leading-[1.65]">{product.short_description}</p>

          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="border border-[#e6edf3] p-3 rounded-xl">
              <div className="overline">Dimensions</div>
              <div className="font-extrabold text-[#0f1d2b] mt-0.5 text-[14px]">{product.dimensions}</div>
            </div>
            <div className="border border-[#e6edf3] p-3 rounded-xl">
              <div className="overline">Capacité</div>
              <div className="font-extrabold text-[#0f1d2b] mt-0.5 text-[14px]">{product.capacity}</div>
            </div>
          </div>

          <div className="mt-4 border-t border-[#e6edf3] pt-4">
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="font-extrabold text-3xl text-[#0f1d2b]" data-testid="product-price">
                {(product.price * qty).toFixed(2).replace(".", ",")} €
              </div>
              <div className="text-xs text-[#5b6b7a]">soit {product.price.toFixed(2).replace(".", ",")} € / {product.unit}</div>
            </div>

            {/* Qty selector — boutons -1 / +1 + input éditable */}
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#5b6b7a] mb-1.5">Quantité</div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-stretch border border-[#e6edf3] rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => updateQty(-1)}
                    className="h-10 w-10 inline-flex items-center justify-center text-[#0f1d2b] hover:bg-[#f7fafc] border-r border-[#e6edf3]"
                    data-testid="qty-decrease"
                    aria-label="Retirer 1"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    value={qty}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setQty(Number.isNaN(v) || v < 1 ? 1 : v);
                    }}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isNaN(v) || v < 1) setQty(1);
                    }}
                    className="w-20 text-center font-extrabold text-base text-[#0f1d2b] bg-transparent outline-none focus:bg-[#f7fafc] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    data-testid="qty-value"
                    aria-label="Quantité"
                  />
                  <button
                    onClick={() => updateQty(1)}
                    className="h-10 w-10 inline-flex items-center justify-center text-[#0f1d2b] hover:bg-[#f7fafc] border-l border-[#e6edf3]"
                    data-testid="qty-increase"
                    aria-label="Ajouter 1"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Pas de 10 — visible uniquement sur certains produits */}
                {["carton-standard-renforce", "carton-a-livres"].includes(product.slug) && (
                  <div
                    className="inline-flex items-stretch border border-[#e6edf3] rounded-xl overflow-hidden bg-white"
                    data-testid="qty-step-10"
                  >
                    <button
                      onClick={() => updateQty(-10)}
                      className="h-10 px-3 inline-flex items-center gap-1 text-[12px] font-bold text-[#0f1d2b] hover:bg-[#f7fafc] border-r border-[#e6edf3]"
                      data-testid="qty-minus-10"
                      aria-label="Retirer 10"
                    >
                      <Minus className="h-3 w-3" /> 10
                    </button>
                    <button
                      onClick={() => updateQty(10)}
                      className="h-10 px-3 inline-flex items-center gap-1 text-[12px] font-bold text-[#0f1d2b] hover:bg-[#f7fafc]"
                      data-testid="qty-plus-10"
                      aria-label="Ajouter 10"
                    >
                      <Plus className="h-3 w-3" /> 10
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={handleAdd}
              className="mt-3 inline-flex items-center justify-center gap-2 h-10 px-5 bg-[#FF7A00] hover:bg-[#E66E00] text-white rounded-xl text-[13.5px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)]"
              data-testid="product-add-to-cart"
            >
              <Plus className="h-4 w-4" /> Ajouter au panier
            </Button>

            <div className="mt-4 space-y-1.5 text-[13px]">
              <div className="flex items-center gap-2.5 text-[#3a4a5a]"><Truck className="h-4 w-4 text-[#237cb5]" /> Livraison sous 24-48h en Île-de-France</div>
              <div className="flex items-center gap-2.5 text-[#3a4a5a]"><ShieldCheck className="h-4 w-4 text-[#237cb5]" /> Carton de qualité professionnelle</div>
              <div className="flex items-center gap-2.5 text-[#3a4a5a]"><Recycle className="h-4 w-4 text-[#237cb5]" /> 100% recyclable, kraft écologique</div>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM — Description longue + highlights */}
      {(product.long_description || product.highlights) && (
        <section className="border-t border-[#e6edf3] bg-white">
          <div className="container-cdm py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="overline">Description</div>
              <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
                {product.name}
              </h2>
              {product.long_description && (
                <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
                  {product.long_description.split("\n\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              )}
            </div>

            {product.highlights && product.highlights.length > 0 && (
              <div className="lg:col-span-5">
                <div className="bg-[#EEF3F7] rounded-2xl p-6 sm:p-8">
                  <div className="overline">Points forts</div>
                  <h3 className="mt-2 text-xl font-extrabold text-[#0f1d2b]">Pourquoi choisir ce carton</h3>
                  <ul className="mt-5 space-y-3" data-testid="product-highlights">
                    {product.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3 text-[15px] text-[#0f1d2b] leading-relaxed">
                        <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
